import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

/* ─── Icon micro-components ──────────────────────────────── */
const Ic = ({ size = 15, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IcEdit   = () => <Ic><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ic>;
const IcTrash  = () => <Ic><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></Ic>;
const IcSearch = () => <Ic><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ic>;
const IcClose  = () => <Ic size={16}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ic>;
const IcPlus   = () => <Ic size={15}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Ic>;
const IcArrowLeft = () => <Ic size={14}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></Ic>;

/* ─── Helpers ─────────────────────────────────────────────── */
const toSlug = (name) =>
  name.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const EMPTY_FORM = { name: '', slug: '', description: '', display_order: 0 };

/* ─── Skeleton loader ─────────────────────────────────────── */
const Skeleton = () => (
  <div className="divide-y divide-slate-100">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="flex items-center gap-4 px-6 py-4">
        <div className="h-3 w-6 bg-slate-200 rounded animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-44 bg-slate-200 rounded animate-pulse" />
          <div className="h-2.5 w-28 bg-slate-100 rounded animate-pulse" />
        </div>
        <div className="h-5 w-16 bg-slate-100 rounded-full animate-pulse" />
        <div className="h-6 w-20 bg-slate-100 rounded animate-pulse" />
      </div>
    ))}
  </div>
);

/* ─── Empty state ─────────────────────────────────────────── */
const EmptyState = ({ onAdd, hasFilter }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-6">
    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 mb-4">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-400">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    </div>
    <p className="text-[14px] font-semibold text-slate-700">
      {hasFilter ? 'No subcategories match' : 'No subcategories yet'}
    </p>
    <p className="text-[13px] text-slate-400 mt-1 max-w-xs">
      {hasFilter ? 'Try adjusting your search or filter.' : 'Create the first subcategory for this category.'}
    </p>
    {!hasFilter && (
      <button onClick={onAdd} className="mt-5 flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-[13px] font-medium rounded-lg hover:bg-blue-700 transition-colors">
        <IcPlus /> New Subcategory
      </button>
    )}
  </div>
);

/* ─── Main component ──────────────────────────────────────── */
const SubcategoryManagement = ({ categoryId, categoryName, onBack }) => {
  const { accessToken } = useAuth();

  const [category, setCategory]       = useState(null);
  const [subcategories, setSubs]      = useState([]);
  const [loading, setLoading]         = useState(true);
  const [savingForm, setSavingForm]   = useState(false);
  const [toast, setToast]             = useState(null);
  const [showPanel, setShowPanel]     = useState(false);
  const [editingSub, setEditingSub]   = useState(null);
  const [confirmDel, setConfirmDel]   = useState(null);
  const [searchQ, setSearchQ]         = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm]               = useState(EMPTY_FORM);
  const [slugEdited, setSlugEdited]   = useState(false);

  const flash = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── Data ── */
  useEffect(() => {
    if (accessToken && categoryId) load();
  }, [categoryId, accessToken]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`https://vmc-backend-vt2w.onrender.com/api/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to load category');
      const { data } = await res.json();
      setCategory(data);
      setSubs(data.subcategories || []);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Panel ── */
  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingSub(null);
    setSlugEdited(false);
    setShowPanel(true);
  };
  const openEdit = (sub) => {
    setForm({ name: sub.name, slug: sub.slug, description: sub.description || '', display_order: sub.display_order ?? 0 });
    setEditingSub(sub);
    setSlugEdited(true);
    setShowPanel(true);
  };
  const closePanel = () => {
    setShowPanel(false);
    setEditingSub(null);
    setForm(EMPTY_FORM);
    setSlugEdited(false);
  };

  /* ── Form ── */
  const onNameChange = (v) =>
    setForm((f) => ({ ...f, name: v, slug: slugEdited ? f.slug : toSlug(v) }));

  const onSlugChange = (v) => {
    setForm((f) => ({ ...f, slug: v }));
    setSlugEdited(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavingForm(true);
    try {
      const isEdit = !!editingSub;
      const res = await fetch(
        isEdit
          ? `https://vmc-backend-vt2w.onrender.com/api/categories/subcategories/${editingSub.id}`
          : `https://vmc-backend-vt2w.onrender.com/api/categories/${categoryId}/subcategories`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) throw new Error('Failed to save subcategory');
      await load();
      closePanel();
      flash('success', isEdit ? 'Subcategory updated.' : 'Subcategory created.');
    } catch (e) {
      flash('error', e.message);
    } finally {
      setSavingForm(false);
    }
  };

  /* ── Delete ── */
  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      const res = await fetch(`https://vmc-backend-vt2w.onrender.com/api/categories/subcategories/${confirmDel.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to delete subcategory');
      await load();
      flash('success', `"${confirmDel.name}" deleted.`);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setConfirmDel(null);
    }
  };

  /* ── Derived ── */
  const filtered = subcategories.filter((s) => {
    const q = searchQ.toLowerCase();
    const textOk = s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q);
    const statusOk = filterStatus === 'all' || (filterStatus === 'active' ? s.is_active : !s.is_active);
    return textOk && statusOk;
  });

  const stats = {
    total: subcategories.length,
    active: subcategories.filter((s) => s.is_active).length,
    inactive: subcategories.filter((s) => !s.is_active).length,
  };

  const hasFilter = !!searchQ || filterStatus !== 'all';
  const displayName = category?.name || categoryName || 'Category';
  const displayIcon = category?.icon || '📁';

  /* ── Render ── */
  return (
    <>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-[70] flex items-center gap-2.5 rounded-xl px-4 py-3 text-[13px] font-medium border shadow-lg shadow-black/10
          ${toast.type === 'success' ? 'bg-white border-emerald-200 text-emerald-700' : 'bg-white border-red-200 text-red-600'}`}>
          {toast.type === 'success'
            ? <svg className="shrink-0 text-emerald-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            : <svg className="shrink-0 text-red-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          }
          {toast.msg}
        </div>
      )}

      {/* ── Parent category info card ── */}
      <div className="bg-white rounded-xl border border-slate-200/80 px-6 py-5 mb-5 flex items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[22px] leading-none select-none">
          {displayIcon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-semibold text-slate-800 leading-snug">{displayName}</p>
          {category?.description && (
            <p className="text-[13px] text-slate-500 mt-0.5 truncate">{category.description}</p>
          )}
        </div>
        <button
          onClick={onBack}
          className="flex shrink-0 items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <IcArrowLeft /> Back to categories
        </button>
      </div>

      {/* ── Subcategories card ── */}
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-200">
          {/* Stats */}
          <div className="flex items-center gap-4">
            <span className="text-[13px] text-slate-500">
              <span className="font-semibold text-slate-800">{stats.total}</span> total
            </span>
            <span className="w-px h-4 bg-slate-200" />
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {stats.active} active
            </span>
            <span className="w-px h-4 bg-slate-200" />
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
              {stats.inactive} inactive
            </span>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <IcSearch />
              </span>
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search subcategories…"
                className="pl-8 pr-3 py-1.5 w-52 text-[13px] text-slate-700 placeholder-slate-400 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2.5 py-1.5 text-[13px] text-slate-700 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
            >
              <option value="all">All status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              onClick={openNew}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 text-white text-[13px] font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              <IcPlus /> New Subcategory
            </button>
          </div>
        </div>

        {/* Table / States */}
        {loading ? (
          <Skeleton />
        ) : filtered.length === 0 ? (
          <EmptyState onAdd={openNew} hasFilter={hasFilter} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70">
                    <th className="pl-6 pr-2 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-10">#</th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Subcategory</th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 hidden lg:table-cell">Description</th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 hidden md:table-cell">Order</th>
                    <th className="pl-3 pr-6 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((sub, i) => (
                    <tr key={sub.id} className="group hover:bg-slate-50/60 transition-colors">
                      <td className="pl-6 pr-2 py-3.5 text-[11px] font-mono text-slate-400">{i + 1}</td>
                      <td className="px-3 py-3.5">
                        <p className="font-semibold text-slate-800 leading-snug">{sub.name}</p>
                        <p className="text-[11.5px] font-mono text-slate-400 mt-0.5">{sub.slug}</p>
                      </td>
                      <td className="px-3 py-3.5 hidden lg:table-cell max-w-[240px]">
                        <p className="text-slate-500 truncate">{sub.description || <span className="text-slate-300">—</span>}</p>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11.5px] font-medium
                          ${sub.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${sub.is_active ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                          {sub.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 text-slate-400 tabular-nums hidden md:table-cell">{sub.display_order ?? '—'}</td>
                      <td className="pl-3 pr-6 py-3.5">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            onClick={() => openEdit(sub)}
                            title="Edit subcategory"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                          >
                            <IcEdit />
                          </button>
                          <button
                            onClick={() => setConfirmDel({ id: sub.id, name: sub.name })}
                            title="Delete subcategory"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <IcTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40">
              <p className="text-[12px] text-slate-400">
                Showing <span className="font-medium text-slate-600">{filtered.length}</span> of{' '}
                <span className="font-medium text-slate-600">{subcategories.length}</span> subcategories
              </p>
            </div>
          </>
        )}
      </div>

      {/* ── Form drawer ─────────────────────────────────── */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] transition-opacity duration-200
          ${showPanel ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={closePanel}
      />
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-[460px] max-w-full bg-white flex flex-col shadow-2xl shadow-black/15
          transition-transform duration-[240ms] ease-[cubic-bezier(0.4,0,0.2,1)]
          ${showPanel ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Panel header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-200 shrink-0">
          <div>
            <p className="text-[15px] font-semibold text-slate-800">
              {editingSub ? 'Edit Subcategory' : 'New Subcategory'}
            </p>
            <p className="text-[12.5px] text-slate-500 mt-0.5">
              {editingSub ? 'Make changes to this subcategory.' : `Creating under "${displayName}".`}
            </p>
          </div>
          <button
            onClick={closePanel}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors mt-0.5"
          >
            <IcClose />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-5">

            {/* Name */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g., Fine Dining"
                className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                Slug <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <span className="px-3 py-2 text-[12px] text-slate-400 bg-slate-50 border-r border-slate-200 font-mono select-none shrink-0 leading-5">
                  /
                </span>
                <input
                  type="text"
                  required
                  value={form.slug}
                  onChange={(e) => onSlugChange(e.target.value)}
                  placeholder="fine-dining"
                  className="flex-1 px-3 py-2 text-[13px] text-slate-800 font-mono placeholder-slate-400 bg-white focus:outline-none"
                />
              </div>
              <p className="text-[11.5px] text-slate-400 mt-1">Auto-generated from name. Used in URLs.</p>
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Display Order</label>
              <input
                type="number"
                min="0"
                value={form.display_order}
                onChange={(e) => setForm((f) => ({ ...f, display_order: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 text-[13px] text-slate-800 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                placeholder="Brief description of this subcategory…"
                className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={closePanel}
              className="px-4 py-2 text-[13px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingForm}
              className="px-5 py-2 text-[13px] font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {savingForm ? 'Saving…' : editingSub ? 'Update Subcategory' : 'Create Subcategory'}
            </button>
          </div>
        </form>
      </aside>

      {/* ── Delete confirm modal ─────────────────────── */}
      {confirmDel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/25 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl shadow-black/10 p-6 w-full max-w-[360px]">
            <div className="flex items-start gap-4 mb-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 mt-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Delete subcategory</p>
                <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">
                  Delete <span className="font-semibold text-slate-700">"{confirmDel.name}"</span>? This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2.5">
              <button
                onClick={() => setConfirmDel(null)}
                className="px-4 py-2 text-[13px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                className="px-4 py-2 text-[13px] font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubcategoryManagement;
